/**
 * The canonical connector catalog.
 *
 * This is the ONLY list of systems Doppler can read from. The "Connect to Data
 * Room" popup renders it, and each entry is matched to a live
 * `source_registry` row by `sourceSystem` so an already-connected system shows
 * a tick instead of inviting a redundant re-connect.
 *
 * `sourceSystem` is the backend's key and does not always match `id`: the
 * sandbox registers email-shaped comms as `email`, which the API humanises to
 * "Outlook".
 *
 * The catalog drives the connect dialog and nothing else. It does NOT drive the
 * deal-room sidebar: connector data never renders there (epic locked decision
 * 4), so no entry here causes a per-provider fetch of anything.
 */
import affinityIcon from "../assets/affinity.png";
import boxUploadIcon from "../assets/box_upload.svg";
import dealcloudIcon from "../assets/dealcloud-mark.svg";
import driveUploadIcon from "../assets/google_drive_upload.svg";
import dropboxUploadIcon from "../assets/dropbox_upload.svg";
import outlookIcon from "../assets/outlook.webp";
import sharepointUploadIcon from "../assets/sharepoint_upload.svg";
import slackIcon from "../assets/slack.webp";

export interface DataSource {
	id: string;
	name: string;
	icon: string;
	description?: string;
	color: string;
	/**
	 * Whether this system is offered as a connect target — i.e. whether an
	 * OAuth/sync handler exists for it. Independent of whether the system
	 * already has data: a connected Box shows a tick AND stays selectable (as
	 * a reconnect), while an unbuilt connector renders a muted "Coming soon"
	 * row even when a demo tenant seeded rows for it.
	 */
	isAvailable: boolean;
	category: "cloud-storage" | "enterprise" | "file-sharing" | "comms" | "crm";
	/** Key used to match a `source_registry` row. */
	sourceSystem: string;
	/**
	 * The backend `CloudProvider` value this tile's connection is keyed by —
	 * the key `GET /integrations/connections` answers in and the freeze (epic
	 * decision 1) enforces on. Its ONLY job is to decide whether a tile is
	 * already connected and must therefore stop offering Connect.
	 *
	 * Absent means "no in-app connect path", which is the honest state for a
	 * coming-soon tile: there is no connection to lock, so nothing may key a
	 * request off it.
	 *
	 * It used to be called `hierarchyProvider` and also drove a per-provider
	 * `GET /integrations/hierarchy/{provider}` fan-out that rendered connector
	 * trees under DEAL ROOM. That is gone: connector data never renders in the
	 * sidebar (epic locked decision 4) — it reaches the user through agent
	 * answers, and the sidebar shows manual uploads only. The name went with
	 * the behaviour so the field cannot be read as advertising a hierarchy
	 * nobody serves.
	 */
	cloudProvider?: string;
}

export const DATA_SOURCES: DataSource[] = [
	{
		id: "dealcloud",
		name: "DealCloud",
		description: "Deals, notes, contacts",
		icon: dealcloudIcon,
		color: "#207CEC",
		isAvailable: false,
		category: "crm",
		sourceSystem: "dealcloud",
	},
	{
		id: "affinity",
		name: "Affinity",
		description: "Deals, notes, people",
		icon: affinityIcon,
		color: "#1B5CFF",
		isAvailable: false,
		category: "crm",
		sourceSystem: "affinity",
	},
	{
		id: "outlook",
		name: "Outlook",
		description: "Your mailbox",
		icon: outlookIcon,
		color: "#0078D4",
		isAvailable: false,
		category: "comms",
		// The sandbox registers email-shaped comms under `email`.
		sourceSystem: "email",
	},
	{
		id: "slack",
		name: "Slack",
		description: "Deal channels",
		icon: slackIcon,
		color: "#4A154B",
		isAvailable: false,
		category: "comms",
		sourceSystem: "slack",
	},
	{
		id: "box",
		name: "Box",
		description: "Folders and files",
		icon: boxUploadIcon,
		color: "#0061D5",
		isAvailable: true,
		category: "cloud-storage",
		sourceSystem: "box",
		// Box is connected in-app, so its tile must lock once the firm has a
		// connection. Its files reach the user through agent answers, never
		// through the sidebar (locked decision 4) — removing the old
		// hierarchy fan-out took nothing away from Box support.
		cloudProvider: "box",
	},
	{
		id: "sharepoint",
		name: "SharePoint",
		description: "Document libraries",
		icon: sharepointUploadIcon,
		color: "#0078D4",
		isAvailable: true,
		category: "enterprise",
		sourceSystem: "sharepoint",
		cloudProvider: "sharepoint",
	},
	{
		id: "dropbox",
		name: "Dropbox",
		description: "Team folders",
		icon: dropboxUploadIcon,
		color: "#0061FF",
		isAvailable: true,
		category: "file-sharing",
		sourceSystem: "dropbox",
		cloudProvider: "dropbox",
	},
	{
		id: "google-drive",
		name: "Google Drive",
		description: "Shared drives too",
		icon: driveUploadIcon,
		color: "#4285F4",
		// Listed so customers can see it is on the roadmap and ask for it. There
		// is no Drive connect path: the endpoints and the client were deleted,
		// so this is a muted "Coming soon" row (`DataSourceItem` derives that
		// from `!isAvailable && !hasData`) and nothing else.
		//
		// ⚠ Deliberately carries NO `cloudProvider`. Nothing may key a request
		// off this tile — a provider key here is what previously made the
		// sidebar fire a guaranteed-404 hierarchy request for Drive on every
		// refresh.
		isAvailable: false,
		category: "cloud-storage",
		sourceSystem: "drive",
	},
];

export const getDataSourcesByCategory = (category: DataSource["category"]) => {
	return DATA_SOURCES.filter((source) => source.category === category);
};

export const getDataSourceById = (id: string) => {
	return DATA_SOURCES.find((source) => source.id === id);
};
